export type SpacingSource = 'pixel' | 'imager' | 'imager-scaled';
export type ResolvedSpacing = { row: number; column: number; source: SpacingSource };

const SPACING_VALUE_COUNT = 2;
/** Relative tolerance when comparing pixel spacings. */
const SPACING_TOLERANCE = 0.01;
const PROJECTION_MODALITIES = new Set(['CR', 'DX', 'MG', 'XA']);

export const PROJECTION_TEXT = {
  noCalibration: 'Res. Ref. Las medidas pueden no ser exactas',
  unknown: 'Proyección desconocida',
  detector: 'Proyección: detector',
  scaled: 'Proyección: valor escalado',
} as const;

function splitValue(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') return value.split('\\');
  return typeof value === 'number' ? [value] : [];
}

/** Numbers from a DICOM JSON `Value` array or a backslash-separated string; non-finite entries are dropped. */
function toNumbers(value: unknown): number[] {
  const parts = splitValue(value);
  return parts
    .map((part) => (typeof part === 'string' && part.trim() === '' ? NaN : Number(part)))
    .filter((n) => Number.isFinite(n));
}

/** Exactly two finite numbers > 0 (row, column), else undefined. */
export function parseSpacing(value: unknown): [number, number] | undefined {
  const parts = splitValue(value);
  const numbers = toNumbers(value);
  if (parts.length !== SPACING_VALUE_COUNT || numbers.length !== SPACING_VALUE_COUNT) return undefined;
  const [row, column] = numbers;
  return row > 0 && column > 0 ? [row, column] : undefined;
}

/** Estimated Radiographic Magnification Factor when it is a finite number > 0. */
function parseMagnification(value: unknown): number | undefined {
  const [first] = toNumbers(Array.isArray(value) ? value.slice(0, 1) : value);
  return first > 0 ? first : undefined;
}

/**
 * Mm-per-pixel spacing: Pixel Spacing (0028,0030) first, else Imager Pixel Spacing (0018,1164),
 * divided by the magnification factor (0018,1114) when one is given.
 */
export function resolveSpacing(input: { pixelSpacing?: unknown; imagerPixelSpacing?: unknown; ermf?: unknown }): ResolvedSpacing | undefined {
  const pixel = parseSpacing(input.pixelSpacing);
  if (pixel) return { row: pixel[0], column: pixel[1], source: 'pixel' };
  const imager = parseSpacing(input.imagerPixelSpacing);
  if (!imager) return undefined;
  const ermf = parseMagnification(input.ermf);
  if (ermf) return { row: imager[0] / ermf, column: imager[1] / ermf, source: 'imager-scaled' };
  return { row: imager[0], column: imager[1], source: 'imager' };
}

function sameSpacing(a: number[], b: number[]): boolean {
  return a.length >= 2 && b.length >= 2 && a.every((v, i) => i > 1 || Math.abs(v - b[i]) <= SPACING_TOLERANCE * Math.max(v, b[i]));
}

/**
 * Projection radiography calibration warning (CR/DX/MG/XA), from Pixel Spacing (0028,0030),
 * Imager Pixel Spacing (0018,1164) and Estimated Radiographic Magnification Factor (0018,1114).
 */
export function projectionWarning(modality: string, pixelSpacing: unknown, imagerSpacing: unknown, ermf: unknown): string {
  const pixel = parseSpacing(pixelSpacing) ?? [];
  const imager = parseSpacing(imagerSpacing) ?? [];
  const magnification = parseMagnification(ermf);
  const hasPixel = pixel.length >= 2;
  const hasImager = imager.length >= 2;
  // Whenever the detector spacing is what measurements use, say so, whatever the modality (RF, PX, IO, RG, OT, ...).
  if (!hasPixel && hasImager) return magnification ? PROJECTION_TEXT.scaled : PROJECTION_TEXT.detector;
  if (!PROJECTION_MODALITIES.has(modality)) return '';
  if (!hasPixel && !hasImager) return PROJECTION_TEXT.noCalibration;
  if (!hasImager) return '';
  if (sameSpacing(pixel, imager)) return PROJECTION_TEXT.detector;
  if (magnification && sameSpacing(pixel, imager.map((v) => v / magnification))) return PROJECTION_TEXT.scaled;
  return PROJECTION_TEXT.unknown;
}
